// Pure helpers behind the staff "Podněty" pane (Pavel 2026-07-24: one human
// title per material, notes are the object of the queue, live arrivals).
// Kept free of React so the grouping and liveness rules stay unit-testable.

export type NoteRow = Record<string, unknown>;

export interface FeedbackGroup {
  kind: string;
  /** Newest visible deliverable of the kind: actions and new notes target it. */
  currentId: string;
  version: number;
  open: NoteRow[];
  resolved: NoteRow[];
  latestAt: string;
}

export interface MaterialRef {
  kind: string;
  id: string;
  version: number;
}

/**
 * Rows the beta / feedback workspaces own: the podněty about the Buddy, and
 * nothing else (F63's `surface`, now shipped with the staff detail).
 *
 * The pane used to render the WHOLE client queue on both steps, so a note on
 * the e-mail campaign appeared under the heading "Beta testování /
 * Zablokované" and a third time in its own step (audit P-13). `surface` is
 * the truth; the deliverable fallback covers a row saved before the column
 * existed, which the 0041 backfill has already re-filed by the same rule.
 */
export function isBetaNote(row: NoteRow): boolean {
  const surface = typeof row.surface === "string" ? row.surface : null;
  if (surface) return surface === "beta";
  return !row.deliverableId && !row.deliverableKind;
}

/**
 * Which side of the table wrote a note. A client user carries no staff role;
 * a partner or an admin does (`u.staff_role`, shipped with the staff detail).
 * Rows written before the column travelled fall back to "client", which is
 * what the old single-key heading already claimed - never a worse guess.
 */
export function noteAuthorSide(row: NoteRow): "client" | "team" {
  const role = typeof row.staffRole === "string" ? row.staffRole : null;
  return role === "partner" || role === "admin" ? "team" : "client";
}

/**
 * The heading a note list may honestly carry. "Připomínky klienta" over a note
 * the partner dictated is a false statement about the one fact that decides
 * what to do with it: whose words these are (audit N04). Every row prints its
 * own author, so the heading only has to name the SET.
 */
export function noteListHeadingKey(rows: readonly NoteRow[]): string {
  if (rows.length === 0) return "partnerOS.workspace.feedback";
  const sides = new Set(rows.map(noteAuthorSide));
  if (sides.size > 1) return "partnerOS.workspace.feedbackMixed";
  return sides.has("team") ? "partnerOS.workspace.feedbackTeam" : "partnerOS.workspace.feedback";
}

/**
 * A dictation arrives as one string with its paragraph breaks intact; a single
 * `<p>` throws them away and prints four thousand characters as one block
 * (audit N03). Split on blank lines, and when the speaker never paused, on
 * single newlines - anything that is not a break stays one paragraph.
 */
export function noteParagraphs(text: string): string[] {
  const parts = text.split(/\n\s*\n/).flatMap((block) => block.split(/\n/));
  const trimmed = parts.map((part) => part.trim()).filter(Boolean);
  return trimmed.length > 0 ? trimmed : [text.trim()].filter(Boolean);
}

/**
 * Above this many characters a note is folded behind "Zobrazit celé". Measured
 * on the note that produced the finding: 3 999 characters ran to 730 px at
 * 1440 px and 5 461 px on a phone, with the resolve control stranded at the
 * top. 900 characters is roughly twelve lines of the note column.
 */
export const NOTE_FOLD_CHARS = 900;

/** The material a note belongs to, from its own kind or from its deliverable. */
export function materialKindOf(row: NoteRow, deliverables: NoteRow[]): string | null {
  if (typeof row.deliverableKind === "string" && row.deliverableKind) return row.deliverableKind;
  const id = String(row.deliverableId ?? "");
  if (!id) return null;
  const match = deliverables.find((deliverable) => String(deliverable.id) === id);
  return match ? String(match.kind) : null;
}

// One entry per MATERIAL: the newest visible deliverable of each kind, in the
// deliverables' input order of first appearance.
export function newestDeliverables(deliverables: NoteRow[]): MaterialRef[] {
  const byKind = new Map<string, MaterialRef>();
  for (const row of deliverables) {
    const kind = String(row.kind);
    const version = Number(row.version ?? 0);
    const known = byKind.get(kind);
    if (!known) byKind.set(kind, { kind, id: String(row.id), version });
    else if (version > known.version) { known.id = String(row.id); known.version = version; }
  }
  return [...byKind.values()];
}

// Group notes per MATERIAL (kind), never per deliverable row: two versions of
// one material are one queue, and notes of a superseded (unpublished) version
// stay with their material instead of falling into "other". Same matching the
// material overlay and DeliverablesPane already use.
export function buildFeedbackGroups(
  feedback: NoteRow[],
  deliverables: NoteRow[]
): { groups: FeedbackGroup[]; other: NoteRow[] } {
  const kindById = new Map<string, string>();
  for (const row of deliverables) kindById.set(String(row.id), String(row.kind));
  const newestByKind = new Map<string, MaterialRef>();
  for (const material of newestDeliverables(deliverables)) newestByKind.set(material.kind, material);

  const byKind = new Map<string, NoteRow[]>();
  const other: NoteRow[] = [];
  for (const row of feedback) {
    const kind = typeof row.deliverableKind === "string" && row.deliverableKind
      ? row.deliverableKind
      : kindById.get(String(row.deliverableId ?? "")) ?? null;
    // A note without a material room to open belongs to the plain rest bucket.
    if (kind && newestByKind.has(kind)) byKind.set(kind, [...(byKind.get(kind) ?? []), row]);
    else other.push(row);
  }

  const groups: FeedbackGroup[] = [...byKind.entries()].map(([kind, rows]) => {
    const newest = newestByKind.get(kind)!;
    return {
      kind,
      currentId: newest.id,
      version: newest.version,
      open: rows.filter((row) => String(row.status) === "new"),
      resolved: rows.filter((row) => String(row.status) !== "new"),
      latestAt: rows.reduce((max, row) => {
        const at = String(row.createdAt ?? "");
        return at > max ? at : max;
      }, "")
    };
  });
  // Triage order: materials waiting on us first, then by latest activity.
  groups.sort((a, b) =>
    (Number(b.open.length > 0) - Number(a.open.length > 0))
    || b.latestAt.localeCompare(a.latestAt)
    || a.kind.localeCompare(b.kind));
  return { groups, other };
}

// Which notes arrived since the pane last looked. Only unseen OPEN notes count
// as fresh: an id flipping new -> resolved is progress, not an arrival.
export function diffFreshNoteIds(seen: ReadonlySet<string>, feedback: NoteRow[]): string[] {
  return feedback
    .filter((row) => String(row.status) === "new" && !seen.has(String(row.id)))
    .map((row) => String(row.id));
}

export interface EditRequestState {
  deliverableId: string;
  kind: string | null;
  status: string;
  noteIds: ReadonlySet<string>;
}

// The FULL life of the one edit request a client can have (sales_edit_request
// resource): new (queued for Alex) -> new + served_at (Alex picked it up) ->
// done | failed. The UI never invents a state: everything here is read from
// the resource the server wrote.
export interface EditRequestSurface {
  deliverableId: string;
  kind: string | null;
  status: "new" | "done" | "failed";
  noteIds: ReadonlySet<string>;
  instruction: string | null;
  requestedAt: string | null;
  /** Set by the automation leg the moment it SERVES the request to the editor. */
  servedAt: string | null;
  resolvedAt: string | null;
  failNote: string | null;
  newDeliverableId: string | null;
}

export function editRequestSurface(
  resources: NoteRow[],
  deliverables: NoteRow[]
): EditRequestSurface | null {
  const row = resources.find((item) => String(item.key) === "sales_edit_request");
  if (!row) return null;
  const value = (row.value && typeof row.value === "object" ? row.value : {}) as Record<string, unknown>;
  const deliverableId = String(value.deliverable_id ?? "");
  const status = String(value.status ?? "");
  if (!deliverableId || !["new", "done", "failed"].includes(status)) return null;
  const match = deliverables.find((item) => String(item.id) === deliverableId);
  return {
    deliverableId,
    kind: match ? String(match.kind) : null,
    status: status as EditRequestSurface["status"],
    noteIds: new Set(Array.isArray(value.note_ids) ? (value.note_ids as unknown[]).map(String) : []),
    instruction: typeof value.instruction === "string" && value.instruction ? value.instruction : null,
    requestedAt: typeof value.requested_at === "string" ? value.requested_at : null,
    servedAt: typeof value.served_at === "string" ? value.served_at : null,
    resolvedAt: typeof value.resolved_at === "string" ? value.resolved_at : null,
    failNote: typeof value.note === "string" && value.note ? value.note : null,
    newDeliverableId: typeof value.new_deliverable_id === "string" ? value.new_deliverable_id : null
  };
}

// The running Alex edit (sales_edit_request resource): the pane mirrors the
// overlay's truth - which material is being worked on and which notes are
// already "Odeslána Alexovi".
export function activeEditRequest(
  resources: NoteRow[],
  deliverables: NoteRow[]
): EditRequestState | null {
  const surface = editRequestSurface(resources, deliverables);
  if (!surface || surface.status !== "new") return null;
  return {
    deliverableId: surface.deliverableId,
    kind: surface.kind,
    status: surface.status,
    noteIds: surface.noteIds
  };
}

// ONE note lifecycle, both sides of the product speak it:
// new (editable) -> sent (in the queued request, edit disabled) -> working
// (Alex fetched the request) -> handled (resolved: worked into a version or
// closed by the partner). A note NEVER silently keeps looking new after an
// action - coverage includes the fallback the server really applies: a
// request without picked note ids works ALL open notes of its deliverable.
export type NoteChipState = "new" | "sent" | "working" | "handled" | "dismissed";

export function noteChipState(
  note: { id: unknown; status: unknown; deliverableId?: unknown; createdAt?: unknown },
  request: EditRequestSurface | null
): NoteChipState {
  // O9 (audit F115): a note closed as NOT RELEVANT is not a note that was
  // worked in. One chip for both states told the partner their test podnět had
  // been built into the material.
  if (String(note.status) === "dismissed") return "dismissed";
  if (String(note.status) !== "new") return "handled";
  if (!request || request.status !== "new") return "new";
  const covered = request.noteIds.size > 0
    ? request.noteIds.has(String(note.id))
    // Empty selection = the server serves every open note of the request's
    // deliverable AT SERVE TIME; notes born after the serve are not in.
    : String(note.deliverableId ?? "") === request.deliverableId
      && (!request.servedAt || String(note.createdAt ?? "") <= request.servedAt);
  if (!covered) return "new";
  return request.servedAt ? "working" : "sent";
}

// State filter of the partner queue. "alex" = sent or working.
export type NoteFilter = "all" | "open" | "alex" | "handled";

export function noteMatchesFilter(state: NoteChipState, filter: NoteFilter): boolean {
  if (filter === "all") return true;
  if (filter === "open") return state === "new";
  if (filter === "alex") return state === "sent" || state === "working";
  // A dismissed note is closed, so it belongs with the closed ones: the filter
  // that says "handled" means "no longer waiting for a decision".
  return state === "handled" || state === "dismissed";
}

// Partner triage order: notes that still need a decision first, then queued,
// then running, then closed. API order remains the tie-breaker by timestamp.
const NOTE_STATE_ORDER: Record<NoteChipState, number> = {
  new: 0,
  sent: 1,
  working: 2,
  handled: 3,
  dismissed: 4
};

export function sortNotesByLifecycle<T extends NoteRow>(
  rows: readonly T[],
  stateOf: (row: T) => NoteChipState
): T[] {
  return [...rows].sort((left, right) =>
    NOTE_STATE_ORDER[stateOf(left)] - NOTE_STATE_ORDER[stateOf(right)]
    || String(right.createdAt ?? "").localeCompare(String(left.createdAt ?? ""))
  );
}

export interface OptimisticEditRequest {
  deliverableId: string;
  kind: string | null;
  noteIds: readonly string[];
  instruction: string | null;
  requestedAt: string;
}

// Instant feedback on "Poslat Alexovi": the send handler records what it just
// queued and the UI flips immediately; the poll replaces it with the server
// resource as soon as any NEW request lands (ours, or a concurrent one - the
// server is the single truth either way). An old finished request never
// shadows the fresh optimistic send.
export function mergeOptimisticRequest(
  server: EditRequestSurface | null,
  optimistic: OptimisticEditRequest | null
): EditRequestSurface | null {
  if (!optimistic) return server;
  if (server && (server.status === "new" || (server.requestedAt ?? "") >= optimistic.requestedAt)) return server;
  return {
    deliverableId: optimistic.deliverableId,
    kind: optimistic.kind,
    status: "new",
    noteIds: new Set(optimistic.noteIds),
    instruction: optimistic.instruction,
    requestedAt: optimistic.requestedAt,
    servedAt: null,
    resolvedAt: null,
    failNote: null,
    newDeliverableId: null
  };
}

// Honesty guard for the live strip: a request that sits unmoved for too long
// is probably stuck (editor crash, runner offline). The UI says so and offers
// a re-send instead of pulsing forever. 20 minutes: a normal edit round trips
// in single minutes.
export const EDIT_REQUEST_STUCK_MS = 20 * 60 * 1000;

export function editRequestLooksStuck(request: EditRequestSurface, nowIso: string): boolean {
  if (request.status !== "new") return false;
  const lastMove = request.servedAt ?? request.requestedAt;
  if (!lastMove) return false;
  const last = Date.parse(lastMove);
  const now = Date.parse(nowIso);
  if (!Number.isFinite(last) || !Number.isFinite(now)) return false;
  return now - last > EDIT_REQUEST_STUCK_MS;
}
